import { useEffect, useState } from 'react';

import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useLazyGetPatientSchedulesQuery } from '@/store/slices/api/patientsApiSlice';

import {
  buildScheduleLetterBody,
  loadClinicScheduleTemplateId,
  loadUpcomingScheduleDates,
  ScheduleLetterPatient,
  sendScheduleLetter
} from './scheduleLetter';

interface SendScheduleLetterModalProps {
  opened: boolean;
  onClose: () => void;
  onSent: () => void;
  patientId: string;
  clinicId: string;
  patient: ScheduleLetterPatient;
}

const SendScheduleLetterModal = ({
  opened,
  onClose,
  onSent,
  patientId,
  clinicId,
  patient
}: SendScheduleLetterModalProps) => {
  const [loadSchedules] = useLazyGetPatientSchedulesQuery();
  const [dates, setDates] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoading(true);
    setDates([]);
    setTemplateId('');
    // status "scheduled" is Scheduled and Pending. Older rows still come back, so dates before today are dropped.
    Promise.all([
      loadUpcomingScheduleDates((page, limit) =>
        loadSchedules({ patientId, status: 'scheduled', limit, page, order: 'ASC' })
          .unwrap()
          .then((result) => result.data.map((row) => row.scheduledDate))
          .catch(() => [] as string[])
      ),
      loadClinicScheduleTemplateId(clinicId)
    ])
      .then(([scheduled, template]) => {
        if (cancelled) return;
        setDates(scheduled);
        setTemplateId(template);
      })
      .catch(() => {
        if (cancelled) return;
        setDates([]);
        setTemplateId('');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, loadSchedules, opened, patientId]);

  const built = buildScheduleLetterBody(patient, templateId, dates);
  const message = 'message' in built ? built.message : '';

  const send = async () => {
    if (!('body' in built)) return;
    setSending(true);
    try {
      const result = await sendScheduleLetter(patientId, built.body);
      if (!result.sent) {
        notifications.show({ title: 'Schedule letter', message: result.message, color: 'red' });
        if (result.reason === 'within_30_days') onSent();
        return;
      }
      notifications.show({
        title: 'Schedule letter sent',
        message: 'Another letter cannot be sent for 30 days.',
        color: 'teal'
      });
      onSent();
      onClose();
    } catch {
      notifications.show({
        title: 'Schedule letter',
        message: 'The schedule letter was not sent.',
        color: 'red'
      });
    } finally {
      setSending(false);
    }
  };

  const address = [
    patient.address?.line1,
    patient.address?.line2,
    [patient.address?.city, patient.address?.state, patient.address?.postalCode]
      .filter(Boolean)
      .join(' ')
  ]
    .map((line) => line?.trim())
    .filter(Boolean);

  return (
    <Modal opened={opened} onClose={onClose} title="Send Schedule Letter" centered>
      <Stack gap="sm">
        <Text size="sm">This mails one schedule letter to the address on the chart.</Text>
        <Text size="sm">{address.join(', ') || 'No mailing address on the chart.'}</Text>
        <Text size="sm">
          {loading
            ? 'Checking the schedule…'
            : dates.length
              ? dates.join(', ')
              : 'No upcoming schedule dates.'}
        </Text>
        {!loading && message && (
          <Text size="sm" c="red">
            {message}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="plum"
            loading={sending || loading}
            disabled={Boolean(message)}
            onClick={() => {
              void send();
            }}>
            Send letter
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SendScheduleLetterModal;
